import { Module } from '$shared/constants/Module';
import { Access } from '$shared/constants/Access';
import { Role, UserRoleLabel } from '$shared/constants/Role';
import type { IUserRole } from '$shared/interfaces/api/IUserRole';

export default class UserRole implements IUserRole {
  readonly id: string;
  readonly name: Role;
  readonly permissions: Record<Module, Access>;

  constructor(id: string, name: Role, permissions: Record<Module, Access>) {
    this.id = id;
    this.name = name;
    this.permissions = permissions;
  }

  public get allowViewSensitiveInfo(): boolean {
    return this.permissions?.ViewSensitiveInfo === Access.Allowed;
  }

  public get hideEnrollmentInfo(): boolean {
    return this.permissions?.Enrollments === Access.None;
  }

  hasPermission(module: Module): boolean {
    switch (module) {
      case Module.ProviderRemoval:
        return this.permissions?.ProviderRemoval === Access.Allowed;
      case Module.SensitiveCvoActions:
        return this.permissions?.SensitiveCvoActions === Access.Allowed;
      case Module.ProviderChangeCredentialingStatus:
        return this.permissions?.ProviderChangeCredentialingStatus === Access.Allowed;
      case Module.Enrollments:
        return this.permissions?.Enrollments !== Access.None;
      case Module.BulkMonitoringActions: {
        return this.permissions?.BulkMonitoringActions === Access.Allowed;
      }
      default:
        return false;
    }
  }

  hasWritePermission(module: Module): boolean {
    switch (module) {
      case Module.Integrations:
        return this.permissions?.Integrations === Access.Write;
      case Module.Organization:
        return this.permissions?.Organization === Access.Write;
      case Module.Providers:
        return this.permissions?.Providers === Access.Write;
      case Module.Enrollments:
        return this.permissions?.Enrollments === Access.Write;
      case Module.AuthenticationSettings:
        return this.permissions?.AuthenticationSettings === Access.Write;
      default:
        return false;
    }
  }

  get nameLabel(): string {
    return UserRoleLabel[this.name];
  }

  static from(response: IUserRole): UserRole {
    return new UserRole(response?.id, response?.name, response?.permissions);
  }
}
